import { useEffect } from 'react';
import { track } from '../lib/breadcrumbs';

/**
 * Renderless component — installs a global capture-phase click listener that
 * pushes a `click` breadcrumb for every user click. Lightweight: we capture
 * a short CSS-selector path + the element's visible text only, no payloads.
 *
 * Skips clicks on the bug report modal itself so it doesn't pollute the
 * breadcrumb trail when the tester opens the report form.
 */
function _shortSelector(el) {
  if (!el || el.nodeType !== 1) return '';
  const tag = el.tagName.toLowerCase();
  if (el.id) return `${tag}#${el.id}`;
  const cls = (el.className && typeof el.className === 'string')
    ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
    : '';
  return `${tag}${cls}`;
}

function _selectorPath(el) {
  const parts = [];
  let node = el;
  for (let i = 0; node && i < 4; i++) {
    parts.unshift(_shortSelector(node));
    if (node === document.body) break;
    node = node.parentElement;
  }
  return parts.filter(Boolean).join(' > ');
}

export default function ClickTracker() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = (event) => {
      try {
        const target = event.target;
        if (!target) return;
        // Skip clicks inside the bug report UI itself
        if (target.closest && target.closest('[data-bug-report-root]')) return;
        const text = (target.innerText || target.textContent || '')
          .trim()
          .slice(0, 80);
        track('click', {
          selector: _selectorPath(target),
          text,
        });
      } catch { /* never break user clicks */ }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);
  return null;
}
